import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { MarginProps, space } from 'styled-system';

import { BlockType, Layout } from 'api/cms';
import Box from 'components/Box';
import Divider from 'components/Divider';
import Grid, { GridItem } from 'components/Grid';
import Hide from 'components/Hide';
import ListingCounts from 'components/ListingCounts';
import HideContainer from 'containers/HideContainer';
import {
  breakpoints,
  colors,
  gutterWidth,
  maxWidth,
  sectionPadding,
} from 'theme';

const blocksWithoutHeadingDivider: BlockType[] = [
  'GRAD_CTA_HERO',
  'CTA_HERO',
  'EMAIL_CAMPAIGN_SIGNUP_SINGLE',
  'EMAIL_CAMPAIGN_SIGNUP_DOUBLE',
  'FORM_BUILDER',
  'TWO_TILES',
  'SPLIT_CONTENT',
  'FLASH_CARD_QUIZ',
  'PERSONA_QUIZ',
  'TILE_GRID',
  'SIGN_UP',
  'SCROLL_TRACKING',
];

const canHaveHeadingDivider = ({ type }) =>
  !blocksWithoutHeadingDivider.some((blockType) => type === blockType);

const blocksWithDividers: BlockType[] = [
  'RANDOM_GRAD_PROGRAMS',
  'RECOMMENDED_ACTIONS',
  'THREE_SECTION_SLIDER',
  'FOUR_ARTICLES_HIGHLIGHT',
  'THREE_STEP_CTA',
];

const mustHaveDivider = ({ type }) => blocksWithDividers.includes(type);

const blocksWithoutDividers: BlockType[] = [
  'GRAD_CTA_HERO',
  'CTA_HERO',
  'IDEALIST_DAYS_MAP',
  'STICKY_HEADER_LINKS',
  'STICKY_HEADER_LINKS_GRAD',
  'EMAIL_CAMPAIGN_SIGNUP_SINGLE',
  'EMAIL_CAMPAIGN_SIGNUP_DOUBLE',
  'FORM_BUILDER',
  'FLASH_CARD_QUIZ',
  'SIGN_UP',
  'SCROLL_TRACKING',
];

const getPrevRealBlock = (blocks, i) => {
  let block;
  let j = i;

  const setBlock = () => {
    j -= 1;
    block = blocks[j];
  };

  setBlock();

  while (block && block.type === 'SCROLL_TRACKING') {
    setBlock();
  }

  return block;
};

const getNextRealBlock = (blocks, i) => {
  let block;
  let j = i;

  const setBlock = () => {
    j += 1;
    block = blocks[j];
  };

  setBlock();

  while (block && block.type === 'SCROLL_TRACKING') {
    setBlock();
  }

  return block;
};

const mustNotHaveDivider = ({ type, mustNotHaveDivider: noDivider }) =>
  blocksWithoutDividers.includes(type) || noDivider;

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/jsx-props-no-spreading
const ContentContainer = styled((props) => <Box {...props} component="main" />)`
  overflow: hidden;
  margin-left: auto;
  margin-right: auto;
`;
const Header = styled.header`
  ${space};
`;

const Main = styled.article<MarginProps>`
  ${space};
`;

const Content = styled.div`
  flex: 1 1 auto;
  width: 100%;
`;
const Sidebar = styled.aside`
  width: 100%;
  margin: 0 auto;
  @media (min-width: ${breakpoints[2]}) {
    width: 300px;
    margin: 0;
  }
`;

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const SidebarDivider = () => (
  <Hide lg xl>
    <Box my={['48px', '64px', null, null]} mx="auto">
      <Divider />
    </Box>
  </Hide>
);

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const GridSidebar = ({
  sidebar,
  position,
}: {
  sidebar: ReactNode;
  position: string;
}) => (
  <GridItem
    columnStart={[1, 1, 8, 9]}
    columnEnd={[13, 13, 13, 13]}
    justifySelf="end"
    order={2}
  >
    {position === 'lower' && <SidebarDivider />}
    <Sidebar>{sidebar}</Sidebar>
    {position === 'upper' && <SidebarDivider />}
  </GridItem>
);

type ContentBlock = {
  component: ReactNode;
  id: string;
  type: BlockType;
  width: 'FULL' | 'GRID' | 'INSET' | 'NARROW';
  padding?: 'DEFAULT' | 'NONE';
  mustNotHaveDivider: boolean;
  hasBackground?: boolean;
  isHidden?: boolean;
  isInline?: boolean;
};
const BlockContainer = styled(
  ({
    blocks,
    block,
    index,
    fullWidthLayout,
    showHeadingDivider,
    hideFirstHeadingDivider,
    withSidebar,
    children,
    ...props
  }: {
    id?: string;
    blocks: Array<ContentBlock>;
    block: ContentBlock;
    index: number;
    fullWidthLayout: boolean;
    showHeadingDivider?: boolean;
    hideFirstHeadingDivider?: boolean;
    withSidebar?: boolean;
    children: ReactNode;
  }) => {
    const padding = fullWidthLayout ? [64, null, 63] : [40, null, 60];
    const prevBlock = getPrevRealBlock(blocks, index);
    return (
      <Box
        position="relative"
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line no-nested-ternary
        display={block.isHidden ? 'none' : block.isInline ? 'inline' : null}
        width="100%"
        mb={!block.padding || block.padding === 'DEFAULT' ? padding : 0}
        mt={
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line no-nested-ternary
          withSidebar && index === 0
            ? [0, 0, 0]
            : !block.padding || block.padding === 'DEFAULT'
            ? padding
            : 0
        }
        py={block.hasBackground && padding}
        pt={
          index > 0 &&
          block.hasBackground &&
          prevBlock &&
          prevBlock.hasBackground
            ? [0, 0, 0]
            : null
        }
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      >
        {withSidebar
          ? children
          : (() => {
              switch (block.width) {
                case 'NARROW':
                case 'INSET':
                case 'GRID':
                  if (fullWidthLayout || block.type === 'SPLIT_CONTENT') {
                    return (
                      <Grid>
                        <GridItem
                          columnStart={[1, 1, 1, 1]}
                          columnEnd={[13, 13, 13, 13]}
                        >
                          {children}
                        </GridItem>
                      </Grid>
                    );
                  }

                  return (
                    <Grid>
                      <GridItem
                        columnStart={[1, 1, 1, 2]}
                        columnEnd={[13, 13, 13, 12]}
                      >
                        {children}
                      </GridItem>
                    </Grid>
                  );

                case 'FULL':
                default:
                  return children;
              }
            })()}
      </Box>
    );
  }
)`
  ${(props) =>
    props.showHeadingDivider &&
    `
    h2${props.hideFirstHeadingDivider ? ':not(:first-child)' : ''} {
      width: 100%;
      &::before {
        content: '';
        display: block;
        margin: 0 auto;
        margin-bottom: ${props.fullWidthLayout ? 64 : 40}px;
        width: 100%;
        max-width: 1200px;
        height: 1px;
        background: ${colors.aluminum};
        @media (min-width: ${breakpoints[1]}) {
          margin-bottom: ${props.fullWidthLayout ? 96 : 60}px;
        }
      }
    }
  `}
  ${(props) =>
    props.block.hasBackground &&
    `
    &::before {
      content: '';
      z-index: -1;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: ${colors.fog};
      > * {
        position: relative;
      }
  `}
`;

const BlocksWithContainer = ({
  blocks,
  withSidebar,
  fullWidthLayout,
}: {
  blocks: Array<ContentBlock>;
  withSidebar?: boolean;
  fullWidthLayout?: boolean;
}) =>
  blocks.map((block, index) => {
    const prevBlock = getPrevRealBlock(blocks, index);
    const nextBlock = getNextRealBlock(blocks, index);

    if (block.type === 'ANCHOR') {
      return null;
    }

    return (
      <BlockContainer
        block={block}
        blocks={blocks}
        index={index}
        id={`cms-block-${block.id}`}
        key={block.id}
        fullWidthLayout={fullWidthLayout}
        withSidebar={withSidebar}
        showHeadingDivider={fullWidthLayout && canHaveHeadingDivider(block)}
        hideFirstHeadingDivider={
          index === 0 ||
          (index > 0 &&
            prevBlock &&
            (mustHaveDivider(prevBlock) ||
              mustHaveDivider(block) ||
              mustNotHaveDivider(prevBlock) ||
              prevBlock.hasBackground))
        }
      >
        {fullWidthLayout &&
          index > 0 &&
          mustHaveDivider(block) &&
          prevBlock &&
          !mustNotHaveDivider(prevBlock) &&
          !block.hasBackground && (
            <Box
              maxWidth={1200}
              mb={fullWidthLayout ? [64, null, null, 96] : [40, null, null, 60]}
              mx="auto"
            >
              <Divider light my={0} />
            </Box>
          )}
        {block.component}
        {fullWidthLayout &&
          index < blocks.length - 2 &&
          mustHaveDivider(block) &&
          nextBlock &&
          !mustHaveDivider(nextBlock) &&
          !mustNotHaveDivider(nextBlock) &&
          ((!block.hasBackground && !nextBlock.hasBackground) ||
            (block.hasBackground && nextBlock.hasBackground)) && (
            <Box
              maxWidth={1200}
              mt={fullWidthLayout ? [64, null, null, 96] : [40, null, null, 60]}
              mx="auto"
            >
              <Divider light my={0} />
            </Box>
          )}
      </BlockContainer>
    );
  });

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const ContentLayout = ({
  isStaff,
  belowBlocks,
  layout,
  lowerSidebar,
  header,
  showListingCounts,
  blocks,
  upperSidebar,
  headerImage,
  fullWidthLayout,
  padBottom,
  padTop,
  socialButtons,
  subscribeButton,
  listingCounts,
}: {
  isStaff: boolean;
  belowBlocks?: ReactNode;
  layout: Layout;
  lowerSidebar: ReactNode;
  header?: ReactNode;
  showListingCounts?: boolean;
  blocks: Array<ContentBlock>;
  upperSidebar: ReactNode;
  headerImage?: ReactNode;
  fullWidthLayout?: boolean;
  padBottom: boolean;
  padTop: boolean;
  socialButtons: ReactNode | null | undefined;
  subscribeButton: ReactNode | null | undefined;
  listingCounts: Record<string, number>;
}) => {
  const hasSidebar = lowerSidebar || upperSidebar;
  let firstBlock;
  let i = 0;

  const setFirstBlock = () => {
    firstBlock = [i];
  };

  setFirstBlock();

  while (firstBlock && firstBlock.type === 'SCROLL_TRACKING') {
    i += 1;
    setFirstBlock();
  }

  const firstBlockHasBackground =
    blocks && blocks.length && firstBlock && firstBlock.hasBackground;
  const contentFooter =
    socialButtons || subscribeButton || belowBlocks ? (
      <>
        {(socialButtons || subscribeButton) && (
          <Box mx="-6px" mb={[16, null, 24]} display="flex" id="subscribe">
            {socialButtons}
            {subscribeButton}
          </Box>
        )}
        {belowBlocks && (
          <Box m={sectionPadding} mt={[16, null, 24]}>
            {belowBlocks}
          </Box>
        )}
      </>
    ) : null;
  return (
    <>
      {socialButtons && layout === 'NARROW' && !isStaff && !hasSidebar && (
        <Box
          position="sticky"
          zIndex={1}
          top={75}
          height={0}
          display={['none', null, null, 'flex']}
          justifyContent="flex-end"
          m="0 auto"
          maxWidth={maxWidth[3]}
        >
          <Box
            m={sectionPadding}
            display="flex"
            flexDirection="column"
            alignItems="center"
            width={gutterWidth + 32}
          >
            {socialButtons}
          </Box>
        </Box>
      )}
      <ContentContainer
        data-qa-id="content-container"
        mt={
          showListingCounts && firstBlockHasBackground && [-120, null, null, 0]
        }
        mb={!padBottom ? [-36, null, null, -54] : undefined}
      >
        {(header || headerImage || showListingCounts) && (
          <Header>
            {header && (
              <Box m={sectionPadding}>
                <Grid>
                  <GridItem
                    columnStart={[
                      1,
                      1,
                      1,
                      fullWidthLayout || hasSidebar ? 1 : 2,
                    ]}
                    columnEnd={[
                      13,
                      13,
                      13,
                      fullWidthLayout || hasSidebar ? 13 : 12,
                    ]}
                  >
                    <Box>{header}</Box>
                  </GridItem>
                </Grid>
              </Box>
            )}
            {headerImage && (
              <Box m={sectionPadding}>
                <Grid>
                  <GridItem
                    columnStart={[
                      1,
                      1,
                      1,
                      fullWidthLayout || hasSidebar ? 1 : 2,
                    ]}
                    columnEnd={[
                      13,
                      13,
                      13,
                      fullWidthLayout || hasSidebar ? 13 : 12,
                    ]}
                  >
                    {headerImage}
                  </GridItem>
                </Grid>
              </Box>
            )}
            {showListingCounts && (
              <Box
                mb={firstBlockHasBackground ? -16 : -1}
                bg={firstBlockHasBackground && colors.fog}
                pt={firstBlockHasBackground && [120, null, null, 0]}
              >
                <Box
                  width="100%"
                  position="relative"
                  m="0 auto"
                  maxWidth={maxWidth[3]}
                  px={[gutterWidth / 2, null, null, gutterWidth]}
                  pt={20}
                >
                  {listingCounts ? (
                    <ListingCounts listingCounts={listingCounts} />
                  ) : null}
                  <Divider mt={20} mb={0} light />
                </Box>
              </Box>
            )}
          </Header>
        )}
        <Main
          m={sectionPadding}
          mt={padTop || hasSidebar ? undefined : [0, 0, 0, 0]}
          mb={[0, 0, 0, 0]}
        >
          {upperSidebar || lowerSidebar ? (
            <Grid>
              <HideContainer lg xl noWrapper>
                {upperSidebar && (
                  <GridSidebar sidebar={upperSidebar} position="upper" />
                )}
              </HideContainer>
              <GridItem columnStart={[1, 1, 1, 1]} columnEnd={[13, 13, 8, 9]}>
                <Content
                  data-qa-id="cms-main-content"
                  data-qa-content-layout={layout}
                >
                  {/* @ts-expect-error ts-migrate(2786) FIXME: 'BlocksWithContainer' cannot be used as a JSX comp... Remove this comment to see the full error message */}
                  <BlocksWithContainer
                    blocks={blocks}
                    fullWidthLayout={fullWidthLayout}
                    withSidebar
                  />
                </Content>
                {contentFooter && <Box mt={gutterWidth}>{contentFooter}</Box>}
              </GridItem>
              <HideContainer xs sm md noWrapper>
                {(upperSidebar || lowerSidebar) && (
                  <GridSidebar
                    sidebar={
                      <>
                        {upperSidebar}
                        <Box
                          mt={upperSidebar && lowerSidebar ? '40px' : undefined}
                        >
                          {lowerSidebar}
                        </Box>
                      </>
                    }
                    position="upper"
                  />
                )}
              </HideContainer>
              <HideContainer lg xl noWrapper>
                {lowerSidebar && (
                  <GridSidebar sidebar={lowerSidebar} position="lower" />
                )}
              </HideContainer>
            </Grid>
          ) : (
            <Content
              data-qa-id="cms-main-content"
              data-qa-content-layout={layout}
            >
              {/* @ts-expect-error ts-migrate(2786) FIXME: 'BlocksWithContainer' cannot be used as a JSX comp... Remove this comment to see the full error message */}
              <BlocksWithContainer
                blocks={blocks}
                fullWidthLayout={fullWidthLayout}
              />
              {contentFooter && (
                <Grid>
                  <GridItem
                    columnStart={[1, 1, 1, fullWidthLayout ? 1 : 2]}
                    columnEnd={[13, 13, 13, fullWidthLayout ? 13 : 12]}
                  >
                    <Box mt={gutterWidth}>{contentFooter}</Box>
                  </GridItem>
                </Grid>
              )}
            </Content>
          )}
        </Main>
      </ContentContainer>
    </>
  );
};

export default ContentLayout;
